/**
 * Authenticated app chrome: responsive drawer nav, user menu, impersonation exit, and main content outlet.
 *
 * Where: wraps protected routes inside `App` (`<DashboardLayout><Outlet/></DashboardLayout>`).
 * Uses: `useAuth`, `useImpersonation`, `useNavigate`, MUI drawer/list patterns; `InboxPendingSendProvider` from `@/components/Inbox/InboxPendingSendContext`.
 * Behavior: mobile temporary drawer vs desktop mini-variant; highlights active route; shows notifications shortcut.
 * Invariants: `InboxPendingSendProvider` wraps all `children` so delayed inbox sends survive navigation away from `/inbox`.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Fade,
  Button,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  CorporateFare as CorporateFareIcon,
  Science as ScienceIcon,
  Inbox as InboxIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Stop as StopIcon,
  Notifications as NotificationsIcon,
  Psychology as PsychologyIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/hooks/useImpersonation';
import { teamApi } from '@/api/team';
import { InboxPendingSendProvider } from '@/components/Inbox';

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 80;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  adminOnly?: boolean;
  requiresServiceOwner?: boolean;
}

// Base menu items (badge will be added dynamically for notifications)
const baseMenuItems: MenuItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/teams', label: 'Teams', icon: <CorporateFareIcon />, requiresServiceOwner: true },
  { path: '/experiments', label: 'Experiments', icon: <ScienceIcon /> },
  { path: '/inbox', label: 'Inbox', icon: <InboxIcon />, badge: 207 },
  { path: '/leads', label: 'Leads', icon: <PeopleIcon /> },
  { path: '/users', label: 'Users', icon: <GroupIcon /> },
  { path: '/notifications', label: 'Notifications', icon: <NotificationsIcon /> },
  { path: '/profile', label: 'Profile', icon: <PersonIcon /> },
  { path: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  {
    path: '/admin/commands',
    label: 'AI Commands',
    icon: <SmartToyIcon />,
    adminOnly: true,
  },
  {
    path: '/admin/conversation-signals',
    label: 'Signal Catalog',
    icon: <PsychologyIcon />,
    adminOnly: true,
  },
];

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isImpersonating, loading: impersonationLoading, stopImpersonation } = useImpersonation();
  
  // Check if user has SERVICE_MANAGER role in any project using generic projects endpoint
  const { data: userProjects } = useQuery({
    queryKey: ['user-projects', user?.key],
    queryFn: () => teamApi.getTeams({ max_results: 1, role_name: 'SERVICE_MANAGER' }),
    enabled: !!user && !(user.is_staff || user.is_superuser),
  });

  // Get pending invites count for badge
  const { data: pendingInvitesData } = useQuery({
    queryKey: ['pending-invites-count', user?.key],
    queryFn: () => teamApi.getPendingInvites({ max_results: 1 }),
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds to keep badge updated
  });

  // Get count from total_results or count items if total_results is not available
  const pendingInvitesCount = pendingInvitesData?.output?.total_results ?? 
    (pendingInvitesData?.output?.items?.length || 0);

  // Filter menu items based on access
  const isAdmin = user?.is_staff || user?.is_superuser;
  // Allow any authenticated user to access Teams (and to create teams)
  const hasServiceOwnerRole = (userProjects?.output?.items?.length || 0) > 0;
  const canAccessTeam = isAdmin || hasServiceOwnerRole || !!user;

  // Build menu items with dynamic badges
  const menuItems: MenuItem[] = baseMenuItems.map(item => {
    // Add pending invites badge to notifications
    if (item.path === '/notifications' && pendingInvitesCount > 0) {
      return { ...item, badge: pendingInvitesCount };
    }
    return item;
  });

  const filteredMenuItems = menuItems.filter(item => {
    if (item.path === '/users' && !canAccessTeam) {
      return false;
    }
    if (item.adminOnly && !isAdmin) {
      return false;
    }
    // Teams page: visible to any authenticated user
    if (item.requiresServiceOwner && !isAdmin && !canAccessTeam) {
      return false;
    }
    return true;
  });
  
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Handle mobile sidebar
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getUserInitials = (name: string | null | undefined) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return 'U';
    }
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isActive = (path: string) => {
    return location.pathname === path || 
           (path === '/dashboard' && location.pathname === '/');
  };

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 64,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Fade in={!sidebarCollapsed} timeout={200}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#6366f1', // Purple color from image
                mr: 1,
              }}
            >
              U
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#1f2937', // Dark text color
              }}
            >
              Unblockly
            </Typography>
          </Box>
        </Fade>
        
        {/* Desktop collapse/expand */}
        <IconButton 
          onClick={handleDrawerToggle} 
          size="small"
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      {/* User Card - Modern Design */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          <Avatar
            src={user?.profile_image || undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: '#f3f4f6', // Light grey background
              color: '#6b7280', // Grey text
              border: '2px solid #e5e7eb',
            }}
          >
            {getUserInitials(user?.name || user?.username)}
          </Avatar>
          
          {!sidebarCollapsed && (
            <Box sx={{ minWidth: 0, flex: 1, ml: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: '#6366f1', // Purple color for name
                }}
              >
                {user?.name?.trim() || user?.username || 'User'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                }}
              >
                {user?.email || 'No email'}
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Impersonation Status and Stop Button */}
        {isImpersonating && !sidebarCollapsed && (
          <Box sx={{ mt: 2 }}>
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 1,
                '& .MuiAlert-message': {
                  fontSize: '0.75rem',
                }
              }}
            >
              Impersonating another user
            </Alert>
            <Button
              variant="outlined"
              size="small"
              startIcon={<StopIcon />}
              onClick={stopImpersonation}
              disabled={impersonationLoading}
              fullWidth
              sx={{
                borderColor: '#dc2626',
                color: '#dc2626',
                '&:hover': {
                  borderColor: '#b91c1c',
                  backgroundColor: '#fef2f2',
                },
                fontSize: '0.75rem',
                py: 0.5,
              }}
            >
              {impersonationLoading ? 'Stopping...' : 'Stop Impersonation'}
            </Button>
          </Box>
        )}
        
        {/* Impersonation indicator for collapsed sidebar */}
        {isImpersonating && sidebarCollapsed && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <IconButton
              size="small"
              onClick={stopImpersonation}
              disabled={impersonationLoading}
              sx={{
                color: '#dc2626',
                '&:hover': {
                  backgroundColor: '#fef2f2',
                },
              }}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Menu Items */}
      <List sx={{ flex: 1, py: 1 }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleMenuClick(item.path)}
              selected={isActive(item.path)}
              sx={{
                mx: 1,
                borderRadius: 1,
                mb: 0.5,
                minHeight: 48,
                '&.Mui-selected': {
                  bgcolor: '#f3f4f6', // Light purple background
                  color: '#6366f1', // Purple text
                  '&:hover': {
                    bgcolor: '#e5e7eb',
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#6366f1', // Purple icon
                  },
                },
                '&:hover': {
                  bgcolor: '#f9fafb',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: sidebarCollapsed ? 0 : 40,
                  color: isActive(item.path) ? '#6366f1' : '#6b7280', // Purple for active, grey for inactive
                }}
              >
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="warning">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive(item.path) ? 600 : 400,
                    color: isActive(item.path) ? '#6366f1' : '#374151', // Purple for active, dark grey for inactive
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Footer - Logout */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            minHeight: 48,
            '&:hover': {
              bgcolor: '#fef2f2', // Light red background
              color: '#dc2626', // Red text
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: sidebarCollapsed ? 0 : 40,
              color: 'inherit',
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <InboxPendingSendProvider>
    <Box sx={{ display: 'flex', height: '100dvh', minHeight: 0 }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={sidebarOpen && isMobile}
          onClose={() => setSidebarOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          maxWidth: '100%',
          flexGrow: 1,
          bgcolor: 'background.default',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Mobile hamburger menu button */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ p: 0, flex: 1, minHeight: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
    </InboxPendingSendProvider>
  );
}; 